import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Button, Popconfirm } from 'antd'
import type { ServiceOrder } from '@super-mega/server/contracts/order'
import { refreshMoneyQueries } from '@/features/bill/index.ts'
import { useAccess } from '@/hooks/use-access.ts'
import { useTRPC } from '@/trpc/react.ts'

export function ServiceLifecycleActions({ order, disabled = false }: { order: ServiceOrder; disabled?: boolean }) {
  const access = useAccess()
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const complete = useMutation(trpc.order.service.complete.mutationOptions())
  const cancel = useMutation(trpc.order.service.cancel.mutationOptions())
  const archive = useMutation(trpc.order.service.archive.mutationOptions())
  const actions = { complete, cancel, archive }
  const run = async (action: keyof typeof actions) => {
    if (disabled || pending || !access.can(`order.service.${action}`)) {
      return
    }
    try {
      await actions[action].mutateAsync({ serviceOrderId: order.id })
      await refreshMoneyQueries(cache, trpc)
      const notices = {
        complete: '服务已完成，未结款项请继续在账单中处理',
        cancel: '服务已取消，费用草稿已作废',
        archive: '已归档，可在列表按“已归档”筛选查看'
      }
      void message.success(notices[action])
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '操作失败')
    }
  }
  const pending = Object.values(actions).some((action) => action.isPending)
  const actionDisabled = disabled || pending
  return (
    <>
      {access.can('order.service.complete') &&
        !order.archivedAt &&
        order.status === 'pending' &&
        order.serviceMode === 'once' && (
          <Popconfirm disabled={actionDisabled} title="确认服务已完成？" onConfirm={() => run('complete')}>
            <Button type="primary" disabled={actionDisabled}>
              完成服务
            </Button>
          </Popconfirm>
        )}
      {access.can('order.service.cancel') && !order.archivedAt && order.status === 'pending' && (
        <Popconfirm
          disabled={actionDisabled}
          title="确认取消服务？"
          description="取消后费用草稿作废；已有正式费用或待退金额须先调整并完成退款。"
          onConfirm={() => run('cancel')}
        >
          <Button danger disabled={actionDisabled}>
            取消服务
          </Button>
        </Popconfirm>
      )}
      {access.can('order.service.archive') &&
        !order.archivedAt &&
        ['completed', 'cancelled', 'ended', 'terminated'].includes(order.status) && (
          <Popconfirm
            disabled={actionDisabled}
            title="确认归档服务订单？"
            description="归档后移出默认列表，可通过归档筛选查看；业务状态、合同、账单及未结款项保留。"
            onConfirm={() => run('archive')}
          >
            <Button disabled={actionDisabled}>归档订单</Button>
          </Popconfirm>
        )}
    </>
  )
}
